/**
 * Mancala AI Player
 *
 * Three difficulty levels:
 * - Easy: random valid pit, 500ms delay
 * - Medium: greedy — pick pit that maximizes seeds in store
 *           (including extra turns), 1s delay
 * - Hard: minimax with alpha-beta pruning (depth 6-8),
 *         evaluation = store difference + capture potential + extra turn potential,
 *         2s delay
 */

import {
  PlayerSide,
  MancalaBoardState,
  MancalaDifficulty,
  PITS_PER_SIDE,
} from './types';

export class MancalaAI {
  private difficulty: MancalaDifficulty;
  private pendingTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(difficulty: MancalaDifficulty = 'medium') {
    this.difficulty = difficulty;
  }

  /** Get the delay in ms before the AI makes a move */
  getDelay(): number {
    switch (this.difficulty) {
      case 'easy':
        return 500;
      case 'medium':
        return 1000;
      case 'hard':
        return 2000;
      default:
        return 1000;
    }
  }

  setDifficulty(difficulty: MancalaDifficulty): void {
    this.difficulty = difficulty;
  }

  getDifficulty(): MancalaDifficulty {
    return this.difficulty;
  }

  /**
   * Choose the best pit index for the given player.
   * Returns -1 if no valid moves exist.
   */
  chooseMove(board: MancalaBoardState, player: PlayerSide): number {
    const validMoves = this.getValidMoves(board, player);
    if (validMoves.length === 0) return -1;

    switch (this.difficulty) {
      case 'easy':
        return this.chooseMoveEasy(validMoves);
      case 'medium':
        return this.chooseMoveMedium(board, player, validMoves);
      case 'hard':
        return this.chooseMoveHard(board, player, validMoves);
      default:
        return this.chooseMoveEasy(validMoves);
    }
  }

  /**
   * Schedule an AI move after the configured delay.
   */
  scheduleMove(
    board: MancalaBoardState,
    player: PlayerSide,
    onMove: (pitIndex: number) => void
  ): () => void {
    this.cancelPendingMove();

    const delay = this.getDelay();
    this.pendingTimeout = setTimeout(() => {
      this.pendingTimeout = null;
      const move = this.chooseMove(board, player);
      onMove(move);
    }, delay);

    return () => this.cancelPendingMove();
  }

  cancelPendingMove(): void {
    if (this.pendingTimeout !== null) {
      clearTimeout(this.pendingTimeout);
      this.pendingTimeout = null;
    }
  }

  destroy(): void {
    this.cancelPendingMove();
  }

  // =========================================================================
  // Easy: Random valid pit
  // =========================================================================

  private chooseMoveEasy(validMoves: number[]): number {
    const index = Math.floor(Math.random() * validMoves.length);
    return validMoves[index];
  }

  // =========================================================================
  // Medium: Greedy — pick pit that maximizes store seeds
  // =========================================================================

  private chooseMoveMedium(
    board: MancalaBoardState,
    player: PlayerSide,
    validMoves: number[]
  ): number {
    let bestMove = validMoves[0];
    let bestScore = -Infinity;

    for (const pit of validMoves) {
      const simBoard = this.cloneBoard(board);
      const score = this.simulateGreedy(simBoard, player, pit);
      if (score > bestScore) {
        bestScore = score;
        bestMove = pit;
      }
    }

    return bestMove;
  }

  /**
   * Simulate a move and follow extra turns greedily.
   * Returns the resulting store score for the player.
   */
  private simulateGreedy(
    board: MancalaBoardState,
    player: PlayerSide,
    pitIndex: number
  ): number {
    const sideIndex = player === 'player1' ? 0 : 1;
    const result = this.simulateMove(board, player, pitIndex);

    if (result.extraTurn) {
      // Greedily pick the best follow-up move
      const nextMoves = this.getValidMoves(result.board, player);
      if (nextMoves.length > 0) {
        let bestFollowUp = -Infinity;
        for (const nextPit of nextMoves) {
          const followBoard = this.cloneBoard(result.board);
          const followScore = this.simulateGreedy(followBoard, player, nextPit);
          bestFollowUp = Math.max(bestFollowUp, followScore);
        }
        return bestFollowUp;
      }
    }

    return result.board.stores[sideIndex];
  }

  // =========================================================================
  // Hard: Minimax with alpha-beta pruning
  // =========================================================================

  private chooseMoveHard(
    board: MancalaBoardState,
    player: PlayerSide,
    validMoves: number[]
  ): number {
    const depth = this.getSearchDepth(board);
    let bestMove = validMoves[0];
    let bestScore = -Infinity;

    for (const pit of validMoves) {
      const simBoard = this.cloneBoard(board);
      const result = this.simulateMove(simBoard, player, pit);
      const nextPlayer = result.extraTurn ? player : this.getOpponent(player);
      const isMaxNext = result.extraTurn;

      const score = this.minimax(
        result.board,
        depth - 1,
        -Infinity,
        Infinity,
        isMaxNext,
        player,
        nextPlayer
      );

      if (score > bestScore) {
        bestScore = score;
        bestMove = pit;
      }
    }

    return bestMove;
  }

  /**
   * Determine search depth based on remaining seeds in pits.
   */
  private getSearchDepth(board: MancalaBoardState): number {
    let totalInPits = 0;
    for (let side = 0; side < 2; side++) {
      for (let i = 0; i < PITS_PER_SIDE; i++) {
        totalInPits += board.pits[side][i];
      }
    }

    // Near end-game, search deeper
    if (totalInPits <= 10) return 10;
    if (totalInPits <= 20) return 8;
    return 6;
  }

  private minimax(
    board: MancalaBoardState,
    depth: number,
    alpha: number,
    beta: number,
    isMaximizing: boolean,
    aiPlayer: PlayerSide,
    currentPlayer: PlayerSide
  ): number {
    // Terminal conditions
    if (depth === 0 || this.isTerminal(board)) {
      return this.evaluateBoard(board, aiPlayer);
    }

    const validMoves = this.getValidMoves(board, currentPlayer);

    if (validMoves.length === 0) {
      // Game over — collect remaining and evaluate
      const finalBoard = this.cloneBoard(board);
      this.collectRemainingSeeds(finalBoard);
      return this.evaluateFinalBoard(finalBoard, aiPlayer);
    }

    if (isMaximizing) {
      let maxEval = -Infinity;
      for (const pit of validMoves) {
        const simBoard = this.cloneBoard(board);
        const result = this.simulateMove(simBoard, currentPlayer, pit);
        const nextPlayer = result.extraTurn
          ? currentPlayer
          : this.getOpponent(currentPlayer);
        const nextIsMax = result.extraTurn
          ? true
          : nextPlayer === aiPlayer;

        const evalScore = this.minimax(
          result.board,
          depth - 1,
          alpha,
          beta,
          nextIsMax,
          aiPlayer,
          nextPlayer
        );
        maxEval = Math.max(maxEval, evalScore);
        alpha = Math.max(alpha, evalScore);
        if (beta <= alpha) break;
      }
      return maxEval;
    } else {
      let minEval = Infinity;
      for (const pit of validMoves) {
        const simBoard = this.cloneBoard(board);
        const result = this.simulateMove(simBoard, currentPlayer, pit);
        const nextPlayer = result.extraTurn
          ? currentPlayer
          : this.getOpponent(currentPlayer);
        const nextIsMax = result.extraTurn
          ? false
          : nextPlayer === aiPlayer;

        const evalScore = this.minimax(
          result.board,
          depth - 1,
          alpha,
          beta,
          nextIsMax,
          aiPlayer,
          nextPlayer
        );
        minEval = Math.min(minEval, evalScore);
        beta = Math.min(beta, evalScore);
        if (beta <= alpha) break;
      }
      return minEval;
    }
  }

  /**
   * Evaluate the board position for the AI player.
   * Factors: store difference, seeds on own side, capture potential, extra turn potential.
   */
  private evaluateBoard(board: MancalaBoardState, aiPlayer: PlayerSide): number {
    const aiSide = aiPlayer === 'player1' ? 0 : 1;
    const oppSide = aiPlayer === 'player1' ? 1 : 0;

    // Store difference (most important)
    const storeDiff = board.stores[aiSide] - board.stores[oppSide];

    // Seeds on own side vs opponent's side
    let aiSeeds = 0;
    let oppSeeds = 0;
    for (let i = 0; i < PITS_PER_SIDE; i++) {
      aiSeeds += board.pits[aiSide][i];
      oppSeeds += board.pits[oppSide][i];
    }

    // Extra turn potential: count pits where seeds == distance to store
    let extraTurnPotential = 0;
    for (let i = 0; i < PITS_PER_SIDE; i++) {
      const distToStore = PITS_PER_SIDE - i;
      if (board.pits[aiSide][i] === distToStore) {
        extraTurnPotential += 3;
      }
    }

    // Capture potential: count empty pits on own side with seeds in opposite pit
    let capturePotential = 0;
    for (let i = 0; i < PITS_PER_SIDE; i++) {
      if (board.pits[aiSide][i] === 0) {
        const oppositePit = PITS_PER_SIDE - 1 - i;
        if (board.pits[oppSide][oppositePit] > 0) {
          capturePotential += board.pits[oppSide][oppositePit];
        }
      }
    }

    return (
      storeDiff * 10 +
      (aiSeeds - oppSeeds) * 2 +
      extraTurnPotential * 3 +
      capturePotential
    );
  }

  /**
   * Evaluate the final board (game over) — pure store count.
   */
  private evaluateFinalBoard(
    board: MancalaBoardState,
    aiPlayer: PlayerSide
  ): number {
    const aiSide = aiPlayer === 'player1' ? 0 : 1;
    const oppSide = aiPlayer === 'player1' ? 1 : 0;
    const diff = board.stores[aiSide] - board.stores[oppSide];

    if (diff > 0) return 10000 + diff;
    if (diff < 0) return -10000 + diff;
    return 0;
  }

  // =========================================================================
  // Board Simulation Helpers
  // =========================================================================

  private getValidMoves(board: MancalaBoardState, player: PlayerSide): number[] {
    const sideIndex = player === 'player1' ? 0 : 1;
    const moves: number[] = [];
    for (let i = 0; i < PITS_PER_SIDE; i++) {
      if (board.pits[sideIndex][i] > 0) {
        moves.push(i);
      }
    }
    return moves;
  }

  /**
   * Simulate a move on a board (mutates the board in place).
   * Returns whether the move grants an extra turn and the resulting board.
   */
  private simulateMove(
    board: MancalaBoardState,
    player: PlayerSide,
    pitIndex: number
  ): { extraTurn: boolean; captured: boolean; board: MancalaBoardState } {
    const sideIndex = player === 'player1' ? 0 : 1;
    const opponentSideIndex = player === 'player1' ? 1 : 0;

    let seeds = board.pits[sideIndex][pitIndex];
    board.pits[sideIndex][pitIndex] = 0;

    let currentPos = this.toLinearPosition(sideIndex, pitIndex);
    let lastLandingSide = -1;
    let lastLandingPit = -1;
    let landedInStore = false;

    while (seeds > 0) {
      currentPos = (currentPos + 1) % 14;

      // Skip opponent's store
      if (player === 'player1' && currentPos === 13) continue;
      if (player === 'player2' && currentPos === 6) continue;

      const { side, pit, isStore } = this.fromLinearPosition(currentPos);

      if (isStore) {
        board.stores[side] += 1;
        lastLandingSide = side;
        lastLandingPit = -1;
        landedInStore = true;
      } else {
        board.pits[side][pit] += 1;
        lastLandingSide = side;
        lastLandingPit = pit;
        landedInStore = false;
      }

      seeds--;
    }

    const extraTurn = landedInStore && lastLandingSide === sideIndex;

    // Check for capture
    let captured = false;
    if (
      !landedInStore &&
      lastLandingSide === sideIndex &&
      lastLandingPit >= 0 &&
      board.pits[sideIndex][lastLandingPit] === 1
    ) {
      const oppositePit = PITS_PER_SIDE - 1 - lastLandingPit;
      const oppositeSeeds = board.pits[opponentSideIndex][oppositePit];

      if (oppositeSeeds > 0) {
        captured = true;
        board.stores[sideIndex] += 1 + oppositeSeeds;
        board.pits[sideIndex][lastLandingPit] = 0;
        board.pits[opponentSideIndex][oppositePit] = 0;
      }
    }

    return { extraTurn, captured, board };
  }

  private isTerminal(board: MancalaBoardState): boolean {
    const p1Empty = board.pits[0].every(s => s === 0);
    const p2Empty = board.pits[1].every(s => s === 0);
    return p1Empty || p2Empty;
  }

  private collectRemainingSeeds(board: MancalaBoardState): void {
    for (let side = 0; side < 2; side++) {
      for (let i = 0; i < PITS_PER_SIDE; i++) {
        board.stores[side] += board.pits[side][i];
        board.pits[side][i] = 0;
      }
    }
  }

  private getOpponent(player: PlayerSide): PlayerSide {
    return player === 'player1' ? 'player2' : 'player1';
  }

  private cloneBoard(board: MancalaBoardState): MancalaBoardState {
    return {
      pits: board.pits.map(row => [...row]),
      stores: [...board.stores],
    };
  }

  private toLinearPosition(side: number, pit: number): number {
    if (side === 0) return pit;
    return 7 + pit;
  }

  private fromLinearPosition(
    pos: number
  ): { side: number; pit: number; isStore: boolean } {
    if (pos >= 0 && pos <= 5) {
      return { side: 0, pit: pos, isStore: false };
    }
    if (pos === 6) {
      return { side: 0, pit: -1, isStore: true };
    }
    if (pos >= 7 && pos <= 12) {
      return { side: 1, pit: pos - 7, isStore: false };
    }
    return { side: 1, pit: -1, isStore: true };
  }
}
